<script>
import { formatMinskDate } from '@/scripts/tools.js';
import constants from '@/constants';

const EMPTY_VALUE = '—';

export default {
    props: {
        record: {
            type: Object,
            required: true,
        },
    },
    methods: {
        formatDate(dateString) {
            return formatMinskDate(dateString, true);
        },
        formatDuration(seconds) {
            if (seconds === null || seconds === undefined) return EMPTY_VALUE;
            return `${seconds.toFixed(3)}s`;
        },
        truncateText(text, maxLength) {
            if (!text) return EMPTY_VALUE;
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + '...';
        },
        formatValue(value) {
            if (value === null || value === undefined) return EMPTY_VALUE;
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';

            // Handle arrays
            if (Array.isArray(value)) {
                return value.length > 0 ? value.join(', ') : EMPTY_VALUE;
            }

            // Try parsing JSON arrays
            if (typeof value === 'string' && value.trim().startsWith('[')) {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return parsed.join(', ');
                    }
                } catch (e) {
                    // Not valid JSON, continue
                }
            }

            if (typeof value === 'string') return value || EMPTY_VALUE;
            return value;
        },
        formatResultsCount(count) {
            if (count === null || count === undefined) return EMPTY_VALUE;
            return count.toLocaleString();
        },
        openUserEdit() {
            this.$router.push({
                name: constants.ROUTER_NAME.USERANALYTICSWITHUSER,
                params: { userId: this.record.user_id },
            });
        },
    },
};
</script>

<template>
    <tr>
        <td>{{ record.id }}</td>
        <td>
            <a
                href="#"
                @click.prevent="openUserEdit"
                class="user_id_link"
                :aria-label="`View user ${record.user_id}`"
            >
                {{ record.user_id }}
            </a>
        </td>
        <td :title="record.search_query || 'N/A'">
            {{ truncateText(record.search_query || '(filter only)', 30) }}
        </td>
        <td :title="formatValue(record.tags_used)">
            {{ truncateText(formatValue(record.tags_used), 40) }}
        </td>
        <td :title="formatValue(record.providers_used)">
            {{ truncateText(formatValue(record.providers_used), 40) }}
        </td>
        <td :title="formatValue(record.games_used)">
            {{ truncateText(formatValue(record.games_used), 40) }}
        </td>
        <td>{{ formatValue(record.asset_type_used) }}</td>
        <td>{{ formatValue(record.file_format_used) }}</td>
        <td>{{ formatValue(record.isolation_used) }}</td>
        <td>{{ formatValue(record.proportions_used) }}</td>
        <td>{{ formatValue(record.character_used) }}</td>
        <td>{{ formatValue(record.season_event_used) }}</td>
        <td>{{ formatValue(record.theme_used) }}</td>
        <td>{{ formatValue(record.hq_only_used) }}</td>
        <td>{{ formatValue(record.custom_filters_used) }}</td>
        <td>{{ formatValue(record.smart_filters_used) }}</td>
        <td class="td_number">
            {{ formatResultsCount(record.results_count) }}
        </td>
        <td class="td_number">{{ formatDuration(record.waiting_time) }}</td>
        <td class="td_nowrap">{{ formatDate(record.created_at) }}</td>
    </tr>
</template>

<style scoped>
.user_id_link {
    color: var(--main-color);
    text-decoration: none;
    font-weight: 500;
}

.user_id_link:hover {
    text-decoration: underline;
}

.td_number {
    text-align: right;
}

.td_nowrap {
    white-space: nowrap;
}

td {
    padding: 12px 15px;
    vertical-align: middle;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-color);
}

tr:hover {
    background: var(--background-hover);
}
</style>
